/**
 * engine/state.ts — The Beating Heart
 *
 * All roads lead here. Every system reads from this state and writes back
 * through it. There are no hidden globals, no scattered singletons, no
 * spooky action at a distance — just one honest object that knows everything
 * about what's happening in the world right now.
 *
 * The camera lerps. The game clock ticks. Seasons turn. The player's
 * position is always in here, patient, waiting to be read by the renderer,
 * the social system, the Aeterna.
 *
 * "All systems read from this state." — CLAUDE.md Module Boundaries
 */

import { Camera } from "../iso/projection.js";
import { AeternaPresenceState, createPresenceState } from "../aeterna/presence.js";
import type { AeternaConductor } from "../aeterna/conductor.js";
import type { CreatureState } from "../creature/state.js";
import type { ActiveMount } from "../mount/system.js";
import type { VehicleState } from "../vehicle/system.js";
import { DeviceState, createDeviceState } from "../device/state.js";
import type { QuestStatus } from "../narrative/quests.js";
import { type WorldState, createWorldState } from "../world/zones.js";
import { getFootprintTiles } from "../room/furniture.js";
export type { AeternaPresenceState, DeviceState, WorldState };

// ─── World Constants ─────────────────────────────────────────────────────────

/** How fast the camera slides toward its target (0–1 per frame at 60fps) */
const CAMERA_LERP = 0.08;

/** Zoom range: minimum outward view, maximum pixel-art-comfy close-up */
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 3.0;

/** Game hours per real second (a game day = ~24 real minutes) */
const GAME_HOURS_PER_SECOND = 1 / 60;

// ─── Direction ───────────────────────────────────────────────────────────────

/**
 * Eight compass directions for isometric 2D sprites.
 * We use the cardinal/ordinal system that Habbo established — the eight
 * directions you'd point if you were standing in the center of a tile.
 */
export type Direction = "N" | "NE" | "E" | "SE" | "S" | "SW" | "W" | "NW";

// ─── Animation State ─────────────────────────────────────────────────────────

export type AnimState =
  | "idle"       // standing still, subtle breathing cycle
  | "walk"       // moving between tiles
  | "sit"        // seated on furniture
  | "interact"   // reaching out to touch something
  | "emote"      // playing an emote animation
  | "wave"       // greeting
  | "dance"      // specific dance emote
  | "type"       // typing at a desk
  | "sleep"      // sleeping in a bed
  | "device"       // holding up hPhone or hPad (standing)
  | "device_walk"  // walking slowly while looking at phone (one-handed only)
  | "device_sit"   // sitting while holding device (phone or pad)
  | "combat_idle"    // combat ready stance — alert, bouncing on balls of feet
  | "combat_attack"; // attack swing — quick forward lunge, auto-returns to combat_idle

// ─── Character Appearance ────────────────────────────────────────────────────

/**
 * Everything the renderer needs to composite this player's character layers.
 * Each field is a palette index or asset key — the composer turns these into
 * actual pixels at render time, caching until the outfit changes.
 */
export interface CharacterAppearance {
  skinTone: string;          // hex colour for skin layer palette swap
  hairStyle: string;         // hair asset key
  hairColour: string;        // hex colour for hair layer
  outfitTop: string;         // shirt/jacket asset key
  outfitTopColour: string;
  outfitBottom: string;      // pants/skirt asset key
  outfitBottomColour: string;
  shoes: string;
  shoesColour: string;
  faceStyle: string;         // eyes, expression variant
  accessory?: string;        // hat, headband, glasses, etc.
  accessoryColour?: string;
  heldItem?: string;         // what the character is currently holding
  aura?: string;             // optional cosmetic aura effect
}

// ─── Inventory ───────────────────────────────────────────────────────────────

/** Filter tabs used by the inventory panel */
export type InventoryTab = "all" | "furniture" | "clothing" | "materials" | "equipment";

export interface InventoryItem {
  id: string;
  type: "furniture" | "clothing" | "material" | "consumable" | "collectible" | "equipment";
  name: string;
  quantity: number;
  rarity: "common" | "uncommon" | "rare" | "legendary" | "seasonal";
  acquiredAt: number;        // real timestamp
  furnitureTypeId?: string;  // furniture registry ID — set for type=="furniture" items
  equipmentData?: EquipmentData;  // stats for equipment items
}

// ─── Equipment ──────────────────────────────────────────────────────────────

export type EquipmentSlot = "weapon" | "armor" | "accessory" | "shield";

export type WeaponClass = "dagger" | "sword_1h" | "sword_2h" | "axe" | "staff" | "mace";
export type ShieldClass = "buckler" | "kite" | "tower";

export interface WeaponVisual {
  class: WeaponClass;
  bladeColor: string;     // primary material (blade, head, tip)
  handleColor: string;    // grip / shaft
  accentColor: string;    // crossguard, gem, binding, edge highlight
  length: number;         // blade/head length in sprite pixels (10–30)
}

export interface ShieldVisual {
  class: ShieldClass;
  faceColor: string;      // shield face
  rimColor: string;       // edge / reinforcement
  emblemColor: string;    // center emblem or boss
}

export interface EquipmentData {
  slot: EquipmentSlot;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  /** Visual description shown in UI */
  description: string;
  /** Required level to equip */
  levelReq: number;
  /** Weapon visual class — drives rendering and combat pose */
  weaponVisual?: WeaponVisual;
  /** Shield visual class — drives off-hand rendering */
  shieldVisual?: ShieldVisual;
}

export interface EquippedGear {
  weapon: InventoryItem | null;
  armor: InventoryItem | null;
  accessory: InventoryItem | null;
  shield: InventoryItem | null;
}

// ─── Screen Shake ───────────────────────────────────────────────────────────

export interface ScreenShake {
  intensity: number;    // current shake magnitude in pixels
  decay: number;        // how fast it fades (higher = faster)
  phase: number;        // reset on each hit so shake always starts at peak displacement
}

// ─── NPC Dialog System ──────────────────────────────────────────────────────

export interface DialogOption {
  id: string;
  text: string;
  /** Action type when selected */
  action: "continue" | "accept_quest" | "complete_quest" | "shop" | "train" | "duel" | "close";
  /** Quest ID if action is quest-related */
  questId?: string;
  /** Whether this option is disabled (greyed out) */
  disabled?: boolean;
  /** Tooltip for disabled options */
  disabledReason?: string;
}

export interface DialogState {
  /** Whether a dialog panel is currently open */
  open: boolean;
  /** NPC we're talking to */
  npcId: string | null;
  npcName: string | null;
  npcTitle: string | null;
  /** Current dialog text being displayed */
  text: string;
  /** Available response options */
  options: DialogOption[];
  /** Whether to show free-text input */
  showFreeInput: boolean;
  /** Free text input value */
  freeInputValue: string;
  /** Dialog page/step for multi-page conversations */
  page: number;
  /** Timestamp when dialog opened (for animation) */
  openedAt: number;
  /** Currently hovered option index (-1 = none) */
  hoveredOption: number;
}

// ─── Quest UI State ─────────────────────────────────────────────────────────

export interface QuestUIState {
  /** Quest log panel open */
  logOpen: boolean;
  /** Currently selected quest in the log */
  selectedQuestId: string | null;
  /** Active quest tracker IDs (pinned quests shown on HUD) */
  trackedQuestIds: string[];
  /** Quest accept/complete overlay animation timestamp */
  overlayQuestId: string | null;
  overlayAction: "accept" | "complete" | null;
  overlayTime: number;
}

// ─── Quest Giver Mark ───────────────────────────────────────────────────────

/** What marker to show above an NPC's head */
export type QuestGiverMark =
  | "available"        // gold ! — has a quest the player can accept
  | "in_progress"      // silver ? — quest accepted, not yet complete
  | "ready_complete"   // gold ? — quest objectives done, turn in
  | "unavailable"      // grey ! — has quests but player doesn't qualify
  | null;              // no marker

// ─── Arena Ambient Particles ────────────────────────────────────────────────

/** A single arena ambient particle — sand wisp, torch spark, or dust mote */
export interface ArenaParticle {
  x: number;      // screen X
  y: number;      // screen Y
  vx: number;     // velocity X
  vy: number;     // velocity Y
  life: number;   // remaining life (seconds)
  maxLife: number; // total lifespan
  size: number;
  color: string;
  type: "sand" | "spark" | "dust";
}

// ─── Training Dummy Combat State ────────────────────────────────────────────

/** DPS meter — tracks damage per second on a training dummy */
export interface DPSMeter {
  /** Creature ID of the dummy being attacked */
  targetId: string;
  /** Total damage dealt since combat started */
  totalDamage: number;
  /** Combat start timestamp (seconds) */
  startTime: number;
  /** Current DPS (rolling 5-second window) */
  currentDPS: number;
  /** Peak DPS achieved */
  peakDPS: number;
  /** Highest single hit */
  biggestHit: number;
  /** Current combo count */
  combo: number;
  /** Max combo achieved */
  maxCombo: number;
  /** Rolling damage window: [timestamp, damage][] */
  damageWindow: [number, number][];
  /** Whether to show the DPS overlay */
  visible: boolean;
}

/** Per-dummy tracking — how many total hits a dummy has absorbed (persistent per session) */
export interface TrainingDummyState {
  /** Map of creatureId → total hits absorbed this session */
  hitsAbsorbed: Record<string, number>;
  /** Map of creatureId → total damage absorbed this session */
  damageAbsorbed: Record<string, number>;
  /** Active DPS meter (null when not attacking a dummy) */
  dpsMeter: DPSMeter | null;
}

// ─── Quest Tracking ─────────────────────────────────────────────────────────

/** Per-quest progress tracking */
export interface QuestProgress {
  questId: string;
  status: QuestStatus;
  /** Which step IDs have been completed */
  completedSteps: string[];
  /** Overall progress 0–1 (computed from completedSteps / totalSteps) */
  progress: number;
  /** Real timestamp when quest was started */
  startedAt: number;
  /** Real timestamp when quest was completed (0 if not done) */
  completedAt: number;
}

/** A whisper the player has encountered and collected */
export interface CollectedWhisper {
  text: string;
  /** Display string like "Dawn, Day 3" */
  displayTime: string;
  /** Real timestamp when encountered */
  collectedAt: number;
}

// ─── Player ───────────────────────────────────────────────────────────────────

export interface CombatStats {
  hp: number;
  maxHp: number;
  attack: number;
  defense: number;
  level: number;
  xp: number;
  xpToNext: number;
  ember: EmberState;
}

/** Active combat encounter state — null when not in combat */
export interface CombatState {
  active: boolean;
  targetId: string | null;           // creature ID being fought
  lastAttackTime: number;            // timestamp of player's last attack
  lastHitTime: number;               // timestamp of last time player took damage
  combo: number;                     // consecutive hits without missing
  damageNumbers: DamageNumber[];     // floating damage text
  strikeQueued: boolean;             // true = attack animation started, damage pending on frame 1
  strikeQueuedAt: number;            // timestamp when strike was queued (for frame-sync)
  staggerTimer: number;              // >0 = player is flinching from a hit (seconds remaining)
  /** Combat session stats — reset on each new encounter */
  sessionStats: CombatSessionStats;
}

/** Per-encounter combat statistics — DPS tracker, kill streak, avoidance counts */
export interface CombatSessionStats {
  /** Timestamp when combat began (for combat timer display) */
  combatStartedAt: number;
  /** Total damage dealt to creatures this session */
  totalDamageDealt: number;
  /** Total damage taken from creatures this session */
  totalDamageTaken: number;
  /** Rolling DPS — recent damage samples for real-time DPS display */
  dpsWindow: Array<{ damage: number; timestamp: number }>;
  /** Current computed DPS (updated each frame from dpsWindow) */
  currentDps: number;
  /** Kill streak — consecutive kills without dying */
  killStreak: number;
  /** Best kill streak this session */
  bestKillStreak: number;
  /** Kill streak display timer — seconds remaining to show banner */
  killStreakShowTime: number;
  /** Avoidance counters — how many times each mitigation type triggered */
  dodgeCount: number;
  parryCount: number;
  blockCount: number;
  /** Total creatures killed this play session */
  totalKills: number;
}

export type DamageType = "physical" | "fire" | "nature" | "frost" | "shadow" | "heal" | "miss" | "dodge" | "block" | "combo_break";

export interface DamageNumber {
  value: number;
  x: number;                        // screen x
  y: number;                        // screen y
  createdAt: number;                 // timestamp
  isPlayerDamage: boolean;           // true = player took this damage
  isCrit: boolean;
  damageType: DamageType;            // visual style + color
  angle: number;                     // direction of float (radians, 0 = up)
}

// ─── Ability & Skill System ─────────────────────────────────────────────────

export type EmberTree = "flame" | "root" | "wind";

export interface AbilityState {
  id: string;                        // ability ID from skill definitions
  cooldownEnd: number;               // timestamp when cooldown expires (0 = ready)
  charges: number;                   // current charges (for multi-charge abilities)
}

export interface AbilityBarState {
  slots: Array<AbilityState | null>; // 6 ability slots (keys 1-6)
  globalCooldownEnd: number;         // GCD timestamp (0 = ready)
}

export interface SkillTreeProgress {
  flame: number[];                   // array of unlocked ability indices in flame tree
  root: number[];                    // same for root tree
  wind: number[];                    // same for wind tree
  points: number;                    // unspent skill points
}

// ─── Buff / Debuff System ───────────────────────────────────────────────────

export type BuffType =
  | "shield"      // absorbs damage
  | "regen"       // heals over time
  | "haste"       // faster attack speed
  | "might"       // +attack
  | "fortify"     // +defense
  | "thorns"      // reflects damage to attacker
  | "burn"        // fire DoT
  | "poison"      // nature DoT
  | "slow"        // reduced movement/attack speed
  | "frostbite"   // frost DoT + slow
  | "shadow_mark" // increased damage taken
  | "stun";       // cannot act

export interface ActiveBuff {
  id: string;                        // unique instance ID
  type: BuffType;
  name: string;                      // display name
  icon: string;                      // glyph/symbol
  stacks: number;                    // current stack count (1+)
  maxStacks: number;                 // maximum stacks
  expiresAt: number;                 // timestamp when effect ends
  tickInterval: number;              // seconds between ticks (0 = no tick)
  lastTickAt: number;                // timestamp of last tick
  value: number;                     // effect magnitude (damage per tick, shield HP, stat bonus, etc.)
  sourceAbilityId: string | null;    // which ability applied this
  isDebuff: boolean;                 // true = harmful (shows red), false = beneficial (shows blue/gold)
}

// ─── Ember Points (Resource System) ─────────────────────────────────────────

export interface EmberState {
  current: number;                   // current EP (0–max)
  max: number;                       // maximum EP (grows with level)
  regenRate: number;                 // EP per second out of combat
  combatRegenRate: number;           // EP per second in combat (slower)
}

// ─── Emote System ───────────────────────────────────────────────────────────

export interface EmoteWheelState {
  open: boolean;                     // is the radial wheel visible
  openedAt: number;                  // timestamp for entrance animation
  selectedIndex: number;             // currently highlighted slice (-1 = none)
  lastEmoteAt: number;               // cooldown between emotes
}

export interface FloatingEmote {
  symbol: string;                    // emoji/glyph that floats up
  x: number;                         // screen x
  y: number;                         // screen y
  createdAt: number;                 // timestamp
  color: string;                     // tint color
}

// ─── PvP State ──────────────────────────────────────────────────────────────

export interface PvPState {
  flagged: boolean;                    // true = player has opted into PvP
  flaggedAt: number;                   // timestamp when PvP was toggled on
  cooldownUntil: number;               // timestamp when PvP can be toggled off (flaggedAt + 600s)
  aggressor: boolean;                  // true = player attacked someone first
  aggressorUntil: number;              // timestamp when aggressor status expires
  lastPvpCombatAt: number;             // timestamp of last PvP hit dealt or received
  kills: number;                       // PvP kill count this session
  deaths: number;                      // PvP death count this session
  honor: number;                       // accumulated honor from fair PvP kills
  dishonor: number;                    // accumulated dishonor from ganking
  honorRank: number;                   // 0-14 rank (0 = unranked, 14 = Grand Marshal)
}

// ─── Duel State ────────────────────────────────────────────────────────────

export interface DuelState {
  active: boolean;                     // currently in a duel
  opponentId: string | null;           // who we're dueling
  opponentName: string | null;
  pendingChallenge: DuelChallenge | null;  // incoming duel request
  countdown: number;                   // 3-2-1 countdown (0 = fight)
  countdownStart: number;              // timestamp when countdown began
  startedAt: number;                   // timestamp when duel actually started
  winnerId: string | null;             // set when duel ends
  loserHp: number;                     // loser stays at 1 HP
  lastCountdownTick: number;           // last countdown integer that played a drum tick
}

export interface DuelChallenge {
  challengerId: string;
  challengerName: string;
  receivedAt: number;                  // timestamp
  expiresAt: number;                   // auto-decline after 30s
}

// ─── Death / Respawn State ──────────────────────────────────────────────────

export interface DeathState {
  isDead: boolean;                     // true = player is dead
  diedAt: number;                      // timestamp of death
  ghostMode: boolean;                  // true = in spirit/ghost form
  ghostAlpha: number;                  // visual transparency (0.3 = translucent ghost)
  respawnTimer: number;                // seconds until respawn is available
  respawnLocation: { tileX: number; tileY: number; zoneId: string } | null;
  deathPenalty: "xp" | "durability" | "none";  // what type of penalty was applied
  xpLost: number;                      // XP lost on this death (for display)
  canRespawn: boolean;                 // true once respawnTimer reaches 0
  deathCount: number;                  // total deaths this session
}

// ─── Target Frame State ─────────────────────────────────────────────────────

export type TargetType = "creature" | "player" | "npc" | "object";
export type EntityDisposition = "friendly" | "neutral" | "hostile" | "attackable" | "pvp_flagged" | "pvp_aggressor";

// ─── Context Menu State ────────────────────────────────────────────────────

export interface ContextMenuState {
  open: boolean;
  x: number;                           // screen position
  y: number;
  targetId: string | null;
  targetType: TargetType | null;
  targetName: string | null;
  options: ContextMenuOption[];
  hoverIdx: number;                    // which option the mouse is over (-1 = none)
}

export interface ContextMenuOption {
  id: string;                          // action identifier
  label: string;                       // display text
  icon: string;                        // unicode icon
  enabled: boolean;                    // greyed out if false
  color?: string;                      // optional text color override
}

export type TargetMarker = "skull" | "cross" | "star" | "diamond" | "moon" | "triangle" | "circle" | "square" | null;
export type EliteRank = "normal" | "elite" | "rare" | "rare_elite" | "boss";

export interface TargetState {
  id: string | null;                   // entity ID of the current target (null = no target)
  type: TargetType | null;             // what kind of entity
  name: string | null;                 // display name
  level: number;                       // level (0 for non-combat entities)
  hp: number;                          // current HP
  maxHp: number;                       // max HP
  disposition: EntityDisposition;       // determines nameplate color
  selectedAt: number;                  // timestamp of selection
  distance: number;                    // tile distance from player
  eliteRank: EliteRank;               // normal/elite/rare/boss — affects frame decoration
  marker: TargetMarker;               // raid marker icon on this target
  castBar: { name: string; progress: number; duration: number } | null;  // what the target is casting
}

// ─── Focus Target ───────────────────────────────────────────────────────────

export interface FocusTargetState {
  id: string | null;
  type: TargetType | null;
  name: string | null;
  level: number;
  hp: number;
  maxHp: number;
  disposition: EntityDisposition;
  setAt: number;
}

// ─── Party System ────────────────────────────────────────────────────────────

export interface PartyMember {
  id: string;
  name: string;
  hp: number;
  maxHp: number;
  level: number;
  isDead: boolean;
  hasAggro: boolean;                   // true if this member is currently tanking
  distance: number;                    // tile distance from player
  marker: TargetMarker;
  activeBuffs: ActiveBuff[];
}

export interface PartyState {
  members: PartyMember[];              // max 4 members (5 including player)
  leaderId: string | null;             // party leader's ID
  lootMethod: "ffa" | "round_robin" | "need_greed";
  pendingInvite: { fromId: string; fromName: string; receivedAt: number; expiresAt: number } | null;
}

// ─── Death Recap ────────────────────────────────────────────────────────────

export interface DeathRecapEntry {
  sourceName: string;                  // what dealt the damage
  damage: number;
  timestamp: number;                   // seconds
  isCrit: boolean;
  abilityName: string;                 // "Melee", "Fireball", etc.
}

export interface DeathRecapState {
  entries: DeathRecapEntry[];          // last N damage events before death
  visible: boolean;                    // show the recap panel
  diedAt: number;
}

// ─── Other Players (Multiplayer) ────────────────────────────────────────────

export interface OtherPlayerState {
  id: string;
  name: string;
  appearance: CharacterAppearance;
  position: { tileX: number; tileY: number; elevation: number };
  direction: Direction;
  animState: AnimState;
  combat: { hp: number; maxHp: number; level: number };
  pvpFlagged: boolean;
  pvpAggressor: boolean;
  isDead: boolean;
  activeBuffs: ActiveBuff[];
  equipment: EquippedGear;
}

export interface PlayerState {
  id: string;
  name: string;
  appearance: CharacterAppearance;
  position: { tileX: number; tileY: number; elevation: number };
  direction: Direction;
  animState: AnimState;
  inventory: InventoryItem[];
  currency: number;
  reputation: number;
  skills: Record<string, number>;     // skill name → 0–100 proficiency
  // Combat attributes
  combat: CombatStats;
  combatState: CombatState;
  // Equipped gear — provides combat stat bonuses
  equipment: EquippedGear;
  // Ability system — skill bar, buffs, skill tree, emotes
  abilityBar: AbilityBarState;
  activeBuffs: ActiveBuff[];
  skillTree: SkillTreeProgress;
  emoteWheel: EmoteWheelState;
  floatingEmotes: FloatingEmote[];
  // Active emote animation override
  activeEmoteId: string | null;      // currently playing emote ID (null = none)
  activeEmoteUntil: number;          // timestamp when emote animation ends
  // Active buff visual aura
  buffAuraColor: string | null;      // dominant buff glow color (null = no aura)
  buffAuraIntensity: number;         // 0.0 - 1.0 glow intensity
  // PvP flagging system
  pvp: PvPState;
  // Duel system
  duel: DuelState;
  // Death and respawn
  death: DeathState;
  // Target frame — who/what the player is inspecting or attacking
  target: TargetState;
  // Focus target — secondary persistent target (F key)
  focusTarget: FocusTargetState;
  // Party system
  party: PartyState;
  // Death recap — what killed you
  deathRecap: DeathRecapState;
  // Target position for movement interpolation
  targetPosition?: { tileX: number; tileY: number };
  // Movement queue for pathfinding results
  movementQueue?: Array<{ tileX: number; tileY: number }>;
  // How far through the current tile-to-tile step we are (0–1)
  moveProgress: number;
  // Personal devices — hPhone and hPad with custom colors, app state
  device: DeviceState;
  // Quest progression — tracked per quest ID
  quests: QuestProgress[];
  // Collected whispers — memorable Aeterna/room observations
  collectedWhispers: CollectedWhisper[];
  /** Gaze direction in tile-space radians (set by attention engine each frame) */
  gazeAngle: number;
  /** Gaze focus intensity 0=idle 1=locked on (drives cone narrowing on minimap) */
  gazeFocus: number;
}

// ─── Camera ───────────────────────────────────────────────────────────────────

/**
 * Camera state extends the iso/projection Camera with a target for smooth
 * lerping. The camera never snaps — it always glides.
 */
export interface CameraState {
  x: number;
  y: number;
  zoom: number;
  targetX: number;
  targetY: number;
  targetZoom: number;
}

// ─── Room ─────────────────────────────────────────────────────────────────────

export type TileType = "wood" | "stone" | "carpet" | "grass" | "marble" | "dirt" | "water" | "cobblestone" | "snow" | "lava" | "crystal" | "sand" | "stone_dark" | "dock";
export type WallType = "wood" | "stone" | "brick" | "plaster" | "glass" | "stone_dark" | "crystal" | "ornate";

export interface TileCell {
  type: TileType;
  elevation: number;         // height in world units (0 = ground)
  walkable: boolean;
  furniture?: string;        // id of furniture occupying this tile
}

export interface WallSegment {
  x: number;
  y: number;
  side: "N" | "W";          // north-facing or west-facing wall in isometric view
  type: WallType;
  hasWindow: boolean;
  hasDoor: boolean;
}

export interface FurniturePlacement {
  id: string;
  type: string;             // references furniture registry
  tileX: number;
  tileY: number;
  elevation: number;
  direction: Direction;
  // Furniture-specific state (lamp: on/off, plant: growthStage, etc.)
  state: Record<string, unknown>;
}

export interface Room {
  id: string;
  name: string;
  ownerId: string;
  width: number;
  height: number;
  tiles: TileCell[][];       // [y][x] grid
  walls: WallSegment[];
  furniture: FurniturePlacement[];
  // Computed/cached mood values — updated when furniture changes
  mood: {
    warmth: number;          // 0–1, affected by fireplaces, lamps, rugs
    coziness: number;        // 0–1, affected by furniture density, soft items
    energy: number;          // 0–1, affected by arcade cabinets, gyms
    mystery: number;         // 0–1, affected by bookshelves, telescopes, art
    nature: number;          // 0–1, affected by plants, windows, outdoor themes
    brightness: number;      // 0–1, affected by lamps, windows, skylight tiles
    noise: number;           // 0–1, affected by speakers, crowding, machines
  };
}

// ─── UI ──────────────────────────────────────────────────────────────────────

export interface UIState {
  inventoryOpen: boolean;
  aeternaOpen: boolean;
  /** Build/edit mode — furniture becomes selectable, grid overlay shows, interactions disabled */
  editMode: boolean;
  /** Combat stance — player draws weapon and enters combat_idle without a target */
  combatStance: boolean;
  /** Pending build action from HUD button click — consumed by loop next frame */
  pendingBuildAction: "rotate" | "move" | "pickup" | null;
  hoveredTile: { x: number; y: number } | null;
  /** ID of furniture the cursor is currently over (set each frame by renderer) */
  hoveredFurnitureId: string | null;
  /** ID of the currently selected (clicked) furniture — shows the action toolbar */
  selectedFurniture: string | null;
  /** Furniture registry ID currently being placed on a tile (null = not placing) */
  placingFurnitureType: string | null;
  /** Compass direction for the piece being placed; cycles with R key */
  placingDirection: Direction;
  /** Active tab in the inventory panel */
  inventoryTab: InventoryTab;
  /** Set true when a HUD element consumes the frame's click; suppresses movement */
  uiClickConsumed: boolean;
  /** ID of placed furniture whose context menu is open (null = closed) */
  furnitureContextId: string | null;
  /** Screen position of the active context menu */
  furnitureContextX: number;
  furnitureContextY: number;
  /** ID of placed furniture being repositioned (null = not in move mode) */
  movingFurnitureId: string | null;
  /** Original tile position saved for cancel during move mode */
  movingOriginalTile: { tileX: number; tileY: number } | null;
  tooltip: string | null;
  /** Furniture ID set by interact-prompt hit region click; consumed by loop */
  pendingInteractFurnitureId: string | null;
  notifications: Array<{ id: string; text: string; expiresAt: number }>;
  /** PvP stats panel open (H key toggle) */
  pvpStatsOpen: boolean;
  /** Click indicator — pulsing diamond + particles at the destination tile */
  clickIndicator: {
    tileX: number;
    tileY: number;
    time: number;           // timestamp when click happened
    reachable: boolean;     // false = red X flash for blocked tiles
  } | null;
  /** Set by HUD button to request device toggle; loop reads + clears */
  deviceToggleRequest: "phone" | "pad" | null;
  /** If set alongside deviceToggleRequest, device opens directly to this app */
  deviceOpenToApp: string | null;
  /** Queued UI sound requests — consumed by game loop each frame */
  uiSounds: string[];
  /** Full-screen room map overlay (M key / minimap click) */
  mapOpen: boolean;
  /** Map pan offset — user can drag the map around */
  mapPanX: number;
  mapPanY: number;
  /** Whether the device overlay is currently showing */
  deviceOpen: boolean;
  /** Screen-space rect of the device overlay for hit-testing (null when closed) */
  deviceScreenRect: { x: number; y: number; w: number; h: number } | null;
  /** Player character screen position — updated each render frame for device animation origin */
  playerScreenX: number;
  playerScreenY: number;
  /** Screen-space hit regions rebuilt each render frame.
   *  Higher priority checked first. `diamonds` = isometric tile hit test.
   *  If `action` is set, it's executed on click; otherwise `id` is used for selection. */
  hitRegions: Array<{
    id: string;
    priority: number;
    /** Rectangular bounds (toolbar buttons, fallback) */
    x: number; y: number; w: number; h: number;
    /** Isometric diamond tiles — if present, test these instead of rect */
    diamonds?: Array<{ cx: number; cy: number; hw: number; hh: number }>;
    /** Action callback (for toolbar buttons); if absent, id is used for selection */
    action?: (state: GameState) => void;
  }>;
}

// ─── Chat ────────────────────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
  // If present, show as a speech bubble above the character
  bubbleUntil?: number;
}

export interface ChatState {
  messages: ChatMessage[];
  inputOpen: boolean;
  inputValue: string;
  // Most recent messages visible as speech bubbles
  activeBubbles: Array<{ senderId: string; text: string; until: number }>;
  /** When true, chat input routes to AeternaConductor instead of room chat */
  aeternaMode: boolean;
}

// ─── Time ────────────────────────────────────────────────────────────────────

export type Season = "spring" | "summer" | "autumn" | "winter";

export interface TimeState {
  /** Total real milliseconds elapsed since session start */
  real: number;
  /** Game hour of day: 0–23.99 */
  gameHour: number;
  /** Current season — changes every ~6 real days in a shared world */
  season: Season;
}

// ─── Scene ───────────────────────────────────────────────────────────────────

export type Scene =
  | "loading"
  | "title"
  | "character_create"
  | "room"
  | "town"
  | "zone_travel"
  | "transition";

// ─── Toast Notifications ───────────────────────────────────────────────────

export interface Toast {
  id: string;
  text: string;
  type: "loot" | "levelup" | "discovery" | "achievement" | "currency" | "info" | "system" | "pvp" | "death";
  time: number;      // performance.now() / 1000
  duration: number;  // seconds to show (default 3)
}

// ─── Dying Creature ────────────────────────────────────────────────────────

export interface DyingCreature {
  creature: CreatureState;  // snapshot at death
  startTime: number;        // performance.now() / 1000
  duration: number;         // total death anim duration (1.2s)
}

// ─── Aggro Indicator ────────────────────────────────────────────────────────

/** Visual "!" indicator when a hostile creature spots the player */
export interface AggroIndicator {
  creatureId: string;
  time: number;           // performance.now() / 1000 when aggro triggered
}

// ─── Footstep Particle ─────────────────────────────────────────────────────

/** Tiny dust kicked up by footsteps — tile-type dependent */
export interface FootstepParticle {
  x: number;              // screen X
  y: number;              // screen Y
  vx: number;             // velocity X, px/s
  vy: number;             // velocity Y, px/s
  life: number;           // remaining life, seconds
  maxLife: number;         // total life, seconds (for fade curve)
  size: number;           // radius in pixels
  r: number;              // color red   0–255
  g: number;              // color green 0–255
  b: number;              // color blue  0–255
  alpha: number;           // base alpha 0–1
}

// ─── Game State ───────────────────────────────────────────────────────────────

export interface GameState {
  scene: Scene;
  previousScene: Scene | null;
  currentRoom: Room | null;
  player: PlayerState;
  camera: CameraState;
  time: TimeState;
  ui: UIState;
  chat: ChatState;
  // Canvas dimensions (logical pixels, before DPR)
  canvasWidth: number;
  canvasHeight: number;
  // Transition progress (0–1) for scene crossfades
  transitionProgress: number;
  transitionTarget: Scene | null;
  // Whether the tab is currently visible (for tab-away time handling)
  tabVisible: boolean;
  // Aeterna behavioral presence state — drives device visuals and ambient thoughts
  aeterna: AeternaPresenceState;
  // Aeterna conversation conductor — owns the neural pipeline and response generation
  // Null until the game enters the room scene (lazy initialization avoids startup cost)
  aeternaConductor: AeternaConductor | null;
  // Screen shake — combat feedback, intensity decays per frame
  screenShake: ScreenShake;
  // Creatures in the current room — updated each frame
  creatures: CreatureState[];
  // All player-owned creatures (persists across rooms)
  playerCreatures: CreatureState[];
  // Vehicles in the current room
  vehicles: VehicleState[];
  // Currently active mount (null = walking)
  playerMount: ActiveMount | null;
  // Currently active vehicle (null = not in vehicle)
  playerVehicle: VehicleState | null;
  // World zone system — districts, travel, exploration
  world: WorldState;
  // NPCs in the current room
  npcs: NpcState[];
  // NPC dialog system — full dialog panel with options
  dialog: DialogState;
  // Quest UI — log, tracker, quest giver marks
  questUI: QuestUIState;
  // Training dummy combat tracking — DPS meter, combo, hit counts
  trainingDummy: TrainingDummyState;
  // Arena ambient particles — sand wisps, torch sparks
  arenaParticles: ArenaParticle[];
  // Creature aggro "!" indicators — the moment you've been spotted
  aggroIndicators: AggroIndicator[];
  // Footstep dust particles — tile-type dependent micro-particles
  footstepParticles: FootstepParticle[];
  // Creature hit flash timing — maps creatureId → timestamp of last hit
  creatureHitTimes: Map<string, number>;
  // Timestamp (seconds) of the last time the player took damage — drives the red vignette
  lastPlayerHitTime: number;
  // Other players in the room (for multiplayer rendering)
  otherPlayers: OtherPlayerState[];
  // Right-click context menu
  contextMenu: ContextMenuState;
  // Toast notifications — loot drops, level ups, discoveries, achievements
  toasts: Toast[];
  // Dying creatures — playing death animation before removal
  dyingCreatures: DyingCreature[];
}

// ─── NPC ─────────────────────────────────────────────────────────────────────

export interface NpcState {
  id: string;
  name: string;
  title: string;                     // e.g. "Merchant", "Guide", "Blacksmith"
  appearance: CharacterAppearance;
  position: { tileX: number; tileY: number; elevation: number };
  direction: Direction;
  animState: AnimState;
  /** NPC role determines interaction type */
  role: "merchant" | "guide" | "quest_giver" | "guard" | "wanderer" | "instructor" | "arena_master" | "dummy_master";
  /** Quest IDs this NPC offers (for quest giver mark computation) */
  questIds?: string[];
  /** Current quest giver mark (computed each frame) */
  questMark?: QuestGiverMark;
  /** Zone this NPC belongs to */
  zoneId: string;
  /** NPC gaze direction in tile-space radians (set by renderer each frame) */
  gazeAngle: number;
  /** NPC attention focus 0=idle 1=engaged (narrows vision cone on minimap) */
  gazeFocus: number;
  /** Dialogue lines — cycled on interaction */
  dialogue: string[];
  /** Current dialogue index */
  dialogueIndex: number;
  /** Whether player is currently talking to this NPC */
  inConversation: boolean;
  /** Merchant inventory (for merchants) */
  shopItems?: ShopItem[];
}

export interface ShopItem {
  name: string;
  furnitureTypeId?: string;
  price: number;
  rarity: "common" | "uncommon" | "rare" | "legendary";
  description: string;
}

// ─── Default Character ────────────────────────────────────────────────────────

function defaultAppearance(): CharacterAppearance {
  return {
    skinTone: "#d4926a",
    hairStyle: "medium_loose",
    hairColour: "#2c1a0a",
    outfitTop: "cozy_sweater",
    outfitTopColour: "#c4956a",
    outfitBottom: "simple_pants",
    outfitBottomColour: "#4a4a6a",
    shoes: "simple_boots",
    shoesColour: "#3a2a1a",
    faceStyle: "friendly",
  };
}

// ─── Starter Room ────────────────────────────────────────────────────────────

/**
 * The first room a player ever sees — 8×8 wood floor, north and west walls,
 * a window, and a handful of furniture that makes it feel already lived-in.
 * A bed, a desk, the Aeterna device, a rug, a floor lamp, a potted plant.
 *
 * This room is waiting for them. It was waiting before they arrived.
 */
export function createStarterRoom(ownerId: string): Room {
  const W = 8;
  const H = 8;

  // Build the tile grid — all wood floor, all walkable
  const tiles: TileCell[][] = [];
  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      tiles[y][x] = {
        type: "wood",
        elevation: 0,
        walkable: true,
      };
    }
  }

  // North wall (y=0) and west wall (x=0) with windows
  const walls: WallSegment[] = [];
  for (let x = 0; x < W; x++) {
    walls.push({
      x, y: 0, side: "N",
      type: "plaster",
      hasWindow: x === 5,   // a window on the north wall, right side
      hasDoor: false,
    });
  }
  for (let y = 0; y < H; y++) {
    walls.push({
      x: 0, y, side: "W",
      type: "plaster",
      hasWindow: y === 5,   // a window on the west wall, lower section
      hasDoor: y === 7,     // door at the bottom-west corner
    });
  }

  const furniture: FurniturePlacement[] = [
    // Bed along the north wall — headboard against wall, extends south
    { id: "f_bed",   type: "bed",         tileX: 6, tileY: 0, elevation: 0, direction: "N",  state: { slept: false } },
    // Desk against the west wall — cozy study nook
    { id: "f_desk",  type: "desk",        tileX: 0, tileY: 5, elevation: 0, direction: "E",  state: { hasComputer: true } },
    // Aeterna device on the desk surface
    { id: "f_aet",   type: "aeterna",     tileX: 0, tileY: 5, elevation: 1, direction: "E",  state: { active: true, personality: "curious" } },
    // A chair at the desk, facing toward it (west toward desk at tileX:0)
    { id: "f_chair", type: "desk_chair",  tileX: 1, tileY: 5, elevation: 0, direction: "W",  state: { occupied: false } },
    // A rug in the center of the room — the gathering point
    { id: "f_rug",   type: "rug_round",   tileX: 3, tileY: 4, elevation: 0, direction: "S",  state: {} },
    // Floor lamp beside the desk
    { id: "f_lamp",  type: "floor_lamp",  tileX: 0, tileY: 6, elevation: 0, direction: "E",  state: { on: true, intensity: 0.8 } },
    // Potted plant in the back corner where walls meet (corner → snaps both walls)
    { id: "f_plant", type: "potted_fern", tileX: 0, tileY: 0, elevation: 0, direction: "SE", state: { growthStage: 2, waterLevel: 0.7 } },
    // Sofa facing the fireplace — seat faces south toward camera, back toward hearth
    { id: "f_sofa",  type: "sofa",        tileX: 3, tileY: 2, elevation: 0, direction: "S",  state: {} },
    // Fireplace against the north wall, centered — the heart of the room
    { id: "f_fire",  type: "fireplace",   tileX: 3, tileY: 0, elevation: 0, direction: "S",  state: { lit: true } },
    // Bookshelf against the west wall — stories and secrets
    { id: "f_books", type: "bookshelf",   tileX: 0, tileY: 3, elevation: 0, direction: "E",  state: { books: 24 } },
    // Animated box — pipeline-generated test asset
    { id: "f_box",   type: "box_animated", tileX: 2, tileY: 4, elevation: 0, direction: "SE", state: {} },
  ];

  // Mark ALL tiles under furniture as occupied (handles multi-tile footprints)
  for (const f of furniture) {
    if (f.elevation > 0) continue; // elevated items (e.g. aeterna on desk) don't block tiles
    const footprint = getFootprintTiles(f);
    for (const t of footprint) {
      if (t.x >= 0 && t.x < W && t.y >= 0 && t.y < H) {
        tiles[t.y][t.x].furniture = f.id;
        tiles[t.y][t.x].walkable = false;
      }
    }
  }

  return {
    id: "room_starter",
    name: "Your Room",
    ownerId,
    width: W,
    height: H,
    tiles,
    walls,
    furniture,
    mood: { warmth: 0.78, coziness: 0.80, energy: 0.25, mystery: 0.35, nature: 0.15, brightness: 0.55, noise: 0.08 },
  };
}

/**
 * A test zone — 22×20 showroom for visually validating pipeline-generated assets.
 * Left wing (marble floor): pipeline-generated furniture from 3D models.
 * Right wing (wood floor): hand-drawn furniture for comparison.
 * Center aisle: walkway between the two sections.
 *
 * Access in-game with 'T' key or the flask icon near the minimap.
 */
export function createTestZone(ownerId: string): Room {
  const W = 22;
  const H = 20;

  const tiles: TileCell[][] = [];
  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      // Left wing (x 1-9): marble — pipeline-generated assets
      // Center aisle (x 10-11): cobblestone walkway
      // Right wing (x 12-20): wood — hand-drawn assets
      let type: TileType;
      if (x >= 10 && x <= 11) type = "cobblestone";
      else if (x < 10) type = "marble";
      else type = "wood";
      tiles[y][x] = { type, elevation: 0, walkable: true };
    }
  }

  // Walls on north + west with windows
  const walls: WallSegment[] = [];
  for (let x = 0; x < W; x++) {
    walls.push({ x, y: 0, side: "N", type: "plaster", hasWindow: x % 3 === 1, hasDoor: false });
  }
  for (let y = 0; y < H; y++) {
    walls.push({ x: 0, y, side: "W", type: "plaster", hasWindow: y % 4 === 1, hasDoor: y === H - 1 });
  }

  const furniture: FurniturePlacement[] = [
    // ═══════════════════════════════════════════════════════════════════════
    //  LEFT WING — Pipeline-generated assets (marble floor)
    //  These are auto-converted from 3D GLB models via hearth-asset-pipeline
    // ═══════════════════════════════════════════════════════════════════════

    // Row 1: BoxAnimated in all 4 directions (rotation test)
    { id: "tz_box_se",  type: "box_animated",   tileX: 2,  tileY: 1,  elevation: 0, direction: "SE", state: {} },
    { id: "tz_box_sw",  type: "box_animated",   tileX: 4,  tileY: 1,  elevation: 0, direction: "SW", state: {} },
    { id: "tz_box_nw",  type: "box_animated",   tileX: 6,  tileY: 1,  elevation: 0, direction: "NW", state: {} },
    { id: "tz_box_ne",  type: "box_animated",   tileX: 8,  tileY: 1,  elevation: 0, direction: "NE", state: {} },

    // Row 2: Leather Couch (2×1) + Macbook
    { id: "tz_couch",   type: "leather_couch",  tileX: 2,  tileY: 3,  elevation: 0, direction: "S",  state: {} },
    { id: "tz_mac",     type: "macbook",        tileX: 5,  tileY: 3,  elevation: 0, direction: "S",  state: {} },
    { id: "tz_drone",   type: "buster_drone",   tileX: 7,  tileY: 3,  elevation: 0, direction: "SE", state: {} },

    // Row 3: Spaceship (2×2)
    { id: "tz_ship",    type: "spaceship",      tileX: 2,  tileY: 5,  elevation: 0, direction: "SE", state: {} },
    { id: "tz_mac2",    type: "macbook",        tileX: 5,  tileY: 5,  elevation: 0, direction: "E",  state: {} },
    { id: "tz_drone2",  type: "buster_drone",   tileX: 7,  tileY: 5,  elevation: 0, direction: "NE", state: {} },

    // Row 4: Audi RS5 (3×2) — big showpiece
    { id: "tz_audi",    type: "audi_rs5",       tileX: 2,  tileY: 8,  elevation: 0, direction: "SE", state: {} },
    { id: "tz_box_s",   type: "box_animated",   tileX: 6,  tileY: 8,  elevation: 0, direction: "S",  state: {} },

    // Row 5: Couch facing different directions
    { id: "tz_couch2",  type: "leather_couch",  tileX: 2,  tileY: 11, elevation: 0, direction: "E",  state: {} },
    { id: "tz_couch3",  type: "leather_couch",  tileX: 5,  tileY: 11, elevation: 0, direction: "W",  state: {} },
    { id: "tz_ship2",   type: "spaceship",      tileX: 7,  tileY: 11, elevation: 0, direction: "NW", state: {} },

    // Row 6: Stacking test — macbook on coffee table
    { id: "tz_tbl_p",   type: "coffee_table",   tileX: 2,  tileY: 14, elevation: 0, direction: "S",  state: {} },
    { id: "tz_mac_stk", type: "macbook",        tileX: 2,  tileY: 14, elevation: 1, direction: "SE", state: {} },
    { id: "tz_drone3",  type: "buster_drone",   tileX: 4,  tileY: 14, elevation: 0, direction: "S",  state: {} },

    // ═══════════════════════════════════════════════════════════════════════
    //  RIGHT WING — Hand-drawn assets (wood floor)
    //  These are procedurally coded by hand in furniture.ts
    // ═══════════════════════════════════════════════════════════════════════

    // Row 1: Seating
    { id: "tz_sofa",    type: "sofa_oak",       tileX: 13, tileY: 1,  elevation: 0, direction: "S",  state: {} },
    { id: "tz_chair",   type: "chair_wooden",   tileX: 16, tileY: 1,  elevation: 0, direction: "E",  state: {} },
    { id: "tz_bed",     type: "bed_cozy",       tileX: 18, tileY: 1,  elevation: 0, direction: "N",  state: { slept: false } },

    // Row 2: Tables + work
    { id: "tz_desk",    type: "desk_study",     tileX: 13, tileY: 3,  elevation: 0, direction: "S",  state: { hasComputer: true } },
    { id: "tz_coffee",  type: "coffee_table",   tileX: 15, tileY: 3,  elevation: 0, direction: "S",  state: {} },
    { id: "tz_chess",   type: "chess_table",    tileX: 17, tileY: 3,  elevation: 0, direction: "S",  state: {} },

    // Row 3: Storage + shelving
    { id: "tz_books",   type: "bookshelf",      tileX: 13, tileY: 5,  elevation: 0, direction: "E",  state: { books: 24 } },
    { id: "tz_chest",   type: "chest_old",      tileX: 15, tileY: 5,  elevation: 0, direction: "S",  state: {} },
    { id: "tz_piano",   type: "piano_upright",  tileX: 17, tileY: 5,  elevation: 0, direction: "S",  state: {} },

    // Row 4: Lighting
    { id: "tz_fire",    type: "fireplace",      tileX: 13, tileY: 7,  elevation: 0, direction: "S",  state: { lit: true } },
    { id: "tz_lamp",    type: "floor_lamp",     tileX: 15, tileY: 7,  elevation: 0, direction: "S",  state: { on: true, intensity: 0.8 } },
    { id: "tz_candle",  type: "candle",         tileX: 17, tileY: 7,  elevation: 0, direction: "S",  state: { lit: true } },
    { id: "tz_chand",   type: "chandelier",     tileX: 19, tileY: 7,  elevation: 0, direction: "S",  state: {} },

    // Row 5: Decoration
    { id: "tz_plant",   type: "potted_plant",   tileX: 13, tileY: 9,  elevation: 0, direction: "S",  state: {} },
    { id: "tz_fish",    type: "fish_tank",      tileX: 15, tileY: 9,  elevation: 0, direction: "S",  state: {} },
    { id: "tz_crys",    type: "crystal_ball",   tileX: 17, tileY: 9,  elevation: 0, direction: "S",  state: {} },
    { id: "tz_art",     type: "wall_art",       tileX: 19, tileY: 9,  elevation: 0, direction: "S",  state: {} },

    // Row 6: Music + tech
    { id: "tz_record",  type: "record_player",  tileX: 13, tileY: 11, elevation: 0, direction: "S",  state: { playing: true } },
    { id: "tz_speaker", type: "speaker_shelf",  tileX: 15, tileY: 11, elevation: 0, direction: "S",  state: {} },
    { id: "tz_arcade",  type: "arcade_cabinet", tileX: 17, tileY: 11, elevation: 0, direction: "S",  state: {} },

    // Row 7: Kitchen + outdoor
    { id: "tz_stove",   type: "cooking_stove",  tileX: 13, tileY: 13, elevation: 0, direction: "S",  state: {} },
    { id: "tz_counter", type: "kitchen_counter", tileX: 15, tileY: 13, elevation: 0, direction: "S",  state: {} },
    { id: "tz_tree",    type: "tree_potted",    tileX: 17, tileY: 13, elevation: 0, direction: "S",  state: {} },
    { id: "tz_fount",   type: "garden_fountain", tileX: 19, tileY: 13, elevation: 0, direction: "S",  state: {} },

    // Row 8: Rug + special
    { id: "tz_rug",     type: "rug_woven",      tileX: 13, tileY: 15, elevation: 0, direction: "S",  state: {} },
    { id: "tz_lantern", type: "hearth_lantern", tileX: 16, tileY: 15, elevation: 0, direction: "S",  state: {} },

    // ═══════════════════════════════════════════════════════════════════════
    //  CENTER AISLE — Lighting for both wings
    // ═══════════════════════════════════════════════════════════════════════
    { id: "tz_al1",     type: "floor_lamp",     tileX: 10, tileY: 2,  elevation: 0, direction: "E",  state: { on: true, intensity: 1.0 } },
    { id: "tz_al2",     type: "floor_lamp",     tileX: 11, tileY: 6,  elevation: 0, direction: "W",  state: { on: true, intensity: 1.0 } },
    { id: "tz_al3",     type: "floor_lamp",     tileX: 10, tileY: 10, elevation: 0, direction: "E",  state: { on: true, intensity: 1.0 } },
    { id: "tz_al4",     type: "floor_lamp",     tileX: 11, tileY: 14, elevation: 0, direction: "W",  state: { on: true, intensity: 1.0 } },
  ];

  // Mark tiles under furniture as occupied
  for (const f of furniture) {
    if (f.elevation > 0) continue;
    const footprint = getFootprintTiles(f);
    for (const t of footprint) {
      if (t.x >= 0 && t.x < W && t.y >= 0 && t.y < H) {
        tiles[t.y][t.x].furniture = f.id;
        tiles[t.y][t.x].walkable = false;
      }
    }
  }

  return {
    id: "room_test_zone",
    name: "Asset Test Zone",
    ownerId,
    width: W,
    height: H,
    tiles,
    walls,
    furniture,
    mood: { warmth: 0.5, coziness: 0.4, energy: 0.3, mystery: 0.5, nature: 0.1, brightness: 0.7, noise: 0.05 },
  };
}

// ─── Factory ─────────────────────────────────────────────────────────────────

/**
 * Create the initial game state. Called once at boot.
 * Everything starts clean: title scene, no room loaded yet, a player
 * waiting to be named, and a clock ticking from midnight.
 */
export function createGameState(canvasWidth: number, canvasHeight: number): GameState {
  const playerId = `player_${Date.now().toString(36)}`;

  return {
    scene: "title",
    previousScene: null,
    currentRoom: null,

    player: {
      id: playerId,
      name: "Newcomer",
      appearance: defaultAppearance(),
      position: { tileX: 3, tileY: 3, elevation: 0 },
      direction: "S",
      animState: "idle",
      inventory: [
        { id: "inv_bookshelf_1",   type: "furniture", name: "Bookshelf",    quantity: 1, rarity: "common",   acquiredAt: 0, furnitureTypeId: "bookshelf"     },
        { id: "inv_candle_1",      type: "furniture", name: "Candle",       quantity: 3, rarity: "common",   acquiredAt: 0, furnitureTypeId: "candle"        },
        { id: "inv_plant_1",       type: "furniture", name: "Potted Plant", quantity: 2, rarity: "common",   acquiredAt: 0, furnitureTypeId: "potted_plant"  },
        { id: "inv_coffee_1",      type: "furniture", name: "Coffee Table", quantity: 1, rarity: "common",   acquiredAt: 0, furnitureTypeId: "coffee_table"  },
        { id: "inv_rug_1",         type: "furniture", name: "Woven Rug",    quantity: 1, rarity: "uncommon", acquiredAt: 0, furnitureTypeId: "rug_woven"     },
        { id: "inv_lamp_1",        type: "furniture", name: "Floor Lamp",   quantity: 1, rarity: "common",   acquiredAt: 0, furnitureTypeId: "floor_lamp"    },
        { id: "inv_chair_1",       type: "furniture", name: "Wooden Chair", quantity: 2, rarity: "common",   acquiredAt: 0, furnitureTypeId: "chair_wooden"  },
        { id: "inv_crystal_1",     type: "furniture", name: "Crystal Ball", quantity: 1, rarity: "rare",     acquiredAt: 0, furnitureTypeId: "crystal_ball"  },
        { id: "inv_fish_tank_1",   type: "furniture", name: "Fish Tank",    quantity: 1, rarity: "uncommon", acquiredAt: 0, furnitureTypeId: "fish_tank"     },
        { id: "inv_record_1",      type: "furniture", name: "Record Player",quantity: 1, rarity: "uncommon", acquiredAt: 0, furnitureTypeId: "record_player" },
      ] as InventoryItem[],
      currency: 100,    // a little something to start with
      reputation: 0,
      combat: {
        hp: 20,
        maxHp: 20,
        attack: 5,
        defense: 3,
        level: 1,
        xp: 0,
        xpToNext: 50,
        ember: { current: 20, max: 20, regenRate: 2.0, combatRegenRate: 0.5 },
      },
      combatState: {
        active: false,
        targetId: null,
        lastAttackTime: 0,
        lastHitTime: 0,
        combo: 0,
        damageNumbers: [],
        strikeQueued: false,
        strikeQueuedAt: 0,
        staggerTimer: 0,
        sessionStats: {
          combatStartedAt: 0,
          totalDamageDealt: 0,
          totalDamageTaken: 0,
          dpsWindow: [],
          currentDps: 0,
          killStreak: 0,
          bestKillStreak: 0,
          killStreakShowTime: 0,
          dodgeCount: 0,
          parryCount: 0,
          blockCount: 0,
          totalKills: 0,
        },
      },
      equipment: {
        weapon: null,
        armor: null,
        accessory: null,
        shield: null,
      },
      abilityBar: {
        slots: [null, null, null, null, null, null],
        globalCooldownEnd: 0,
      },
      activeBuffs: [],
      skillTree: {
        flame: [],
        root: [],
        wind: [],
        points: 0,
      },
      emoteWheel: {
        open: false,
        openedAt: 0,
        selectedIndex: -1,
        lastEmoteAt: 0,
      },
      floatingEmotes: [],
      activeEmoteId: null,
      activeEmoteUntil: 0,
      buffAuraColor: null,
      buffAuraIntensity: 0,
      pvp: {
        flagged: false,
        flaggedAt: 0,
        cooldownUntil: 0,
        aggressor: false,
        aggressorUntil: 0,
        lastPvpCombatAt: 0,
        kills: 0,
        deaths: 0,
        honor: 0,
        dishonor: 0,
        honorRank: 0,
      },
      duel: {
        active: false,
        opponentId: null,
        opponentName: null,
        pendingChallenge: null,
        countdown: 0,
        countdownStart: 0,
        startedAt: 0,
        winnerId: null,
        loserHp: 1,
        lastCountdownTick: 0,
      },
      death: {
        isDead: false,
        diedAt: 0,
        ghostMode: false,
        ghostAlpha: 1.0,
        respawnTimer: 0,
        respawnLocation: null,
        deathPenalty: "none",
        xpLost: 0,
        canRespawn: false,
        deathCount: 0,
      },
      target: {
        id: null,
        type: null,
        name: null,
        level: 0,
        hp: 0,
        maxHp: 0,
        disposition: "neutral",
        selectedAt: 0,
        distance: 0,
        eliteRank: "normal",
        marker: null,
        castBar: null,
      },
      focusTarget: {
        id: null,
        type: null,
        name: null,
        level: 0,
        hp: 0,
        maxHp: 0,
        disposition: "neutral",
        setAt: 0,
      },
      party: {
        members: [],
        leaderId: null,
        lootMethod: "ffa",
        pendingInvite: null,
      },
      deathRecap: {
        entries: [],
        visible: false,
        diedAt: 0,
      },
      skills: {
        crafting: 1,
        cooking: 1,
        gardening: 1,
        music: 1,
        art: 1,
        design: 1,
        fishing: 1,
        exploration: 1,
      },
      moveProgress: 0,
      device: createDeviceState(),
      quests: [
        // Starter quests — available from the beginning
        { questId: "q_welcome_note", status: "active",  completedSteps: ["s1"], progress: 0.5,  startedAt: Date.now(), completedAt: 0 },
        { questId: "q_first_song",   status: "active",  completedSteps: [],     progress: 0,    startedAt: Date.now(), completedAt: 0 },
        { questId: "q_first_craft",  status: "active",  completedSteps: [],     progress: 0,    startedAt: Date.now(), completedAt: 0 },
        { questId: "q_dawn_watch",   status: "locked",  completedSteps: [],     progress: 0,    startedAt: 0,          completedAt: 0 },
        { questId: "q_duet",         status: "locked",  completedSteps: [],     progress: 0,    startedAt: 0,          completedAt: 0 },
      ],
      collectedWhispers: [
        { text: "You stopped walking for a moment there. I liked that. Stillness is a kind of arrival.", displayTime: "Dusk, Day 1", collectedAt: Date.now() - 86400000 },
        { text: "The walls here remember more than we do. I can feel it — a warmth that isn't from the fire.", displayTime: "Morning, Day 2", collectedAt: Date.now() - 43200000 },
        { text: "Do you think a place can love the people inside it? I think the Hearth tries.", displayTime: "Afternoon, Day 2", collectedAt: Date.now() - 21600000 },
      ],
      gazeAngle: Math.PI / 2, // facing south in tile space
      gazeFocus: 0,
    },

    camera: {
      x: canvasWidth / 2,
      y: canvasHeight / 2,
      zoom: 1.0,
      targetX: canvasWidth / 2,
      targetY: canvasHeight / 2,
      targetZoom: 1.0,
    },

    time: {
      real: 0,
      gameHour: 20.5,   // we start at 8:30pm — warm evening light
      season: "autumn",
    },

    ui: {
      inventoryOpen: false,
      aeternaOpen: false,
      editMode: false,
      combatStance: false,
      pendingBuildAction: null,
      hoveredTile: null,
      hoveredFurnitureId: null,
      selectedFurniture: null,
      placingFurnitureType: null,
      placingDirection: "SE",
      inventoryTab: "all",
      uiClickConsumed: false,
      furnitureContextId: null,
      furnitureContextX: 0,
      furnitureContextY: 0,
      movingFurnitureId: null,
      movingOriginalTile: null,
      tooltip: null,
      pendingInteractFurnitureId: null,
      notifications: [],
      pvpStatsOpen: false,
      clickIndicator: null,
      deviceToggleRequest: null,
      deviceOpenToApp: null,
      uiSounds: [],
      mapOpen: false,
      mapPanX: 0,
      mapPanY: 0,
      deviceOpen: false,
      deviceScreenRect: null,
      playerScreenX: 0,
      playerScreenY: 0,
      hitRegions: [],
    },

    chat: {
      messages: [],
      inputOpen: false,
      inputValue: "",
      activeBubbles: [],
      aeternaMode: false,
    },

    canvasWidth,
    canvasHeight,
    transitionProgress: 0,
    transitionTarget: null,
    tabVisible: true,
    aeterna: createPresenceState(),
    aeternaConductor: null,
    screenShake: { intensity: 0, decay: 12, phase: 0 },
    creatures: [],
    playerCreatures: [],
    vehicles: [],
    playerMount: null,
    playerVehicle: null,
    world: createWorldState(),
    npcs: [],
    dialog: {
      open: false,
      npcId: null,
      npcName: null,
      npcTitle: null,
      text: "",
      options: [],
      showFreeInput: false,
      freeInputValue: "",
      page: 0,
      openedAt: 0,
      hoveredOption: -1,
    },
    questUI: {
      logOpen: false,
      selectedQuestId: null,
      trackedQuestIds: ["q_welcome_note", "q_first_song"],
      overlayQuestId: null,
      overlayAction: null,
      overlayTime: 0,
    },
    trainingDummy: {
      hitsAbsorbed: {},
      damageAbsorbed: {},
      dpsMeter: null,
    },
    arenaParticles: [],
    aggroIndicators: [],
    footstepParticles: [],
    creatureHitTimes: new Map(),
    lastPlayerHitTime: 0,
    otherPlayers: [],
    contextMenu: {
      open: false,
      x: 0,
      y: 0,
      targetId: null,
      targetType: null,
      targetName: null,
      options: [],
      hoverIdx: -1,
    },
    toasts: [],
    dyingCreatures: [],
  };
}

// ─── Camera Update ────────────────────────────────────────────────────────────

/**
 * Smooth camera follow. Runs every frame.
 *
 * The camera doesn't snap — it glides toward its target at 8% per frame.
 * This makes every room transition and player movement feel like the world
 * is being gently pulled along by gravity. It's one of those invisible
 * things players never notice but would immediately miss.
 *
 * Also handles zoom lerping so pinch-to-zoom feels silky.
 */
export function updateCamera(state: GameState, dt: number): void {
  const cam = state.camera;

  // Use dt to keep lerp speed frame-rate independent
  // lerpFactor per frame = 1 - (1 - CAMERA_LERP)^(dt / (1/60))
  const lerpFactor = 1 - Math.pow(1 - CAMERA_LERP, dt * 60);

  cam.x += (cam.targetX - cam.x) * lerpFactor;
  cam.y += (cam.targetY - cam.y) * lerpFactor;
  cam.zoom += (cam.targetZoom - cam.zoom) * lerpFactor;

  // Snap when very close to avoid infinite-approach jitter
  if (Math.abs(cam.x - cam.targetX) < 0.1) cam.x = cam.targetX;
  if (Math.abs(cam.y - cam.targetY) < 0.1) cam.y = cam.targetY;
  if (Math.abs(cam.zoom - cam.targetZoom) < 0.001) cam.zoom = cam.targetZoom;
}

/**
 * Center the camera on a specific world tile position.
 * Call this when entering a room or when you want to focus on something.
 */
export function centerCameraOnTile(
  state: GameState,
  tileX: number,
  tileY: number
): void {
  const cam = state.camera;
  // Project the tile center to screen coords
  const wx = tileX + 0.5;
  const wy = tileY + 0.5;
  const screenX = (wx - wy) * 32; // TILE_W_HALF
  const screenY = (wx + wy) * 16; // TILE_H_HALF

  cam.targetX = state.canvasWidth / 2 - screenX * cam.zoom;
  cam.targetY = state.canvasHeight / 2 - screenY * cam.zoom;
}

// ─── Time Update ─────────────────────────────────────────────────────────────

/**
 * Tick the game clock forward.
 *
 * Time passes even when you're away — a world that stops when you close the
 * tab feels less real. The room mood at midnight is different from noon.
 * The Aeterna can greet you "good morning" because morning is real here.
 */
export function updateTime(state: GameState, dt: number): void {
  state.time.real += dt * 1000;
  state.time.gameHour = (state.time.gameHour + GAME_HOURS_PER_SECOND * dt) % 24;
}

// ─── Scene Transitions ────────────────────────────────────────────────────────

/**
 * Begin a scene transition. The game loop will drive the progress.
 * Don't switch scenes directly — always go through this so the renderer
 * can crossfade gracefully.
 */
export function beginTransition(state: GameState, target: Scene): void {
  if (state.transitionTarget !== null) return; // already transitioning
  state.transitionTarget = target;
  state.transitionProgress = 0;
  state.previousScene = state.scene;
  state.scene = "transition";
}

/**
 * Expose camera as the iso/projection Camera interface so other systems
 * can use projection functions without knowing about state structure.
 */
export function asCamera(state: GameState): Camera {
  return {
    x: state.camera.x,
    y: state.camera.y,
    zoom: state.camera.zoom,
  };
}
